import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthServiceTopic, liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { GrowthPartyLabelService } from './growth-party-label.service';
import { GrowthRetentionPlay, GrowthServiceService } from './growth-service.service';

@Component({
  standalone: true,
  selector: 'ain-growth-retention-plays',
  imports: [AinPageComponent, InfoHintComponent, TableModule, FormsModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('growth.service.retentionPlays.title')" [description]="t('growth.service.retentionPlays.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.service.retentionPlays.help.title'"
        [descriptionKey]="'growth.service.retentionPlays.help.description'"
        [bulletKeys]="['growth.service.retentionPlays.help.bullets.consent', 'growth.service.retentionPlays.help.bullets.live']" />

      <section class="card list-card">
        <div class="toolbar">
          <input
            pInputText
            type="text"
            [(ngModel)]="searchTerm"
            [placeholder]="t('growth.service.retentionPlays.search')" />
          <p-select
            [options]="approvalStateOptions()"
            [(ngModel)]="approvalStateFilter"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            appendTo="body"
            [placeholder]="t('growth.service.retentionPlays.filterApprovalState')" />
          <button pButton type="button" [label]="t('growth.service.retentionPlays.search')" (click)="load()"></button>
          <button pButton type="button" [label]="t('growth.service.retentionPlays.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.service.retentionPlays.addNew')" (click)="openCreate()"></button>
          <button
            pButton
            type="button"
            [label]="t('growth.service.retentionPlays.editSelected')"
            severity="secondary"
            [disabled]="!selected()"
            (click)="openEditSelected()"></button>
        </div>
        <p-table
          [value]="filteredRows()"
          selectionMode="single"
          [selection]="selected()"
          (selectionChange)="onSelect($event)"
          dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.service.retentionPlays.columns.partyId') }}</th>
              <th>{{ t('growth.service.retentionPlays.columns.triggerType') }}</th>
              <th>{{ t('growth.service.retentionPlays.columns.approvalState') }}</th>
              <th>{{ t('growth.service.retentionPlays.columns.consentChecked') }}</th>
              <th>{{ t('growth.service.retentionPlays.columns.channelHint') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.id)">{{ partyLabels.label(row.partyId) }}</button>
              </td>
              <td>{{ triggerLabel(row.triggerType) }}</td>
              <td>{{ approvalLabel(row.approvalState) }}</td>
              <td>{{ row.consentChecked ? t('common.yes') : t('common.no') }}</td>
              <td>{{ channelLabel(row.channelHint) }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
  `]
})
export class GrowthRetentionPlaysComponent implements OnInit {
  private readonly api = inject(GrowthServiceService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly partyLabels = inject(GrowthPartyLabelService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly rows = signal<GrowthRetentionPlay[]>([]);
  readonly filteredRows = signal<GrowthRetentionPlay[]>([]);
  readonly selected = signal<GrowthRetentionPlay | null>(null);
  readonly errorMessage = signal('');

  searchTerm = '';
  approvalStateFilter: string | null = null;

  private subscribedTopic: string | null = null;

  approvalStateOptions(): Array<{ label: string; value: string }> {
    return ['Draft', 'Approved', 'Rejected', 'ExecutedDeferred'].map(value => ({
      value,
      label: this.approvalLabel(value)
    }));
  }

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthServiceChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load();
  }

  triggerLabel(value: string): string {
    return this.t(`growth.service.retentionPlayDetail.triggerType.${value}`);
  }

  channelLabel(value: string): string {
    return this.t(`growth.comms.channel.${value}`);
  }

  approvalLabel(value: string): string {
    return this.t(`growth.service.retentionPlays.approvalState.${value}`);
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listRetentionPlays(undefined, this.approvalStateFilter ?? undefined).subscribe({
      next: data => {
        this.partyLabels.resolve(data.map(item => item.partyId));
        this.rows.set(data);
        this.applySearch();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('growth.service.retentionPlays.messages.loadFailed'));
      }
    });
  }

  applySearch(): void {
    const term = this.searchTerm.trim().toLowerCase();
    const filtered = !term
      ? this.rows()
      : this.rows().filter(item =>
          item.partyId.toLowerCase().includes(term)
          || this.partyLabels.label(item.partyId).toLowerCase().includes(term)
          || item.triggerType.toLowerCase().includes(term)
          || item.channelHint.toLowerCase().includes(term));
    this.filteredRows.set(filtered);
    this.selected.set(filtered[0] ?? null);
  }

  onSelect(row: GrowthRetentionPlay | null): void {
    this.selected.set(row);
  }

  openCreate(): void {
    void this.router.navigate(['/growth/service/retention-plays/new']);
  }

  openEditSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.id);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/growth/service/retention-plays', id]);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthServiceTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
